import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowUpRight, Download, Mail, Linkedin } from "lucide-react";
import resumeAsset from "@/assets/resume.pdf.asset.json";

export const Route = createFileRoute("/resume")({
  head: () => ({
    meta: [
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },

      { title: "Resume | Scott Cunningham" },
      {
        name: "description",
        content:
          "Scott Cunningham (@scottcbusiness) resume: content strategy, technical copywriting, social media leadership, and AI-assisted workflows.",
      },
      { property: "og:title", content: "Resume | Scott Cunningham" },
      {
        property: "og:description",
        content:
          "Scott Cunningham (@scottcbusiness) resume: content strategy, technical copywriting, social media leadership, and AI-assisted workflows.",
      },
      { property: "og:url", content: "https://scottcbusiness.com/resume" },
      { name: "twitter:title", content: "Resume | Scott Cunningham" },
      {
        name: "twitter:description",
        content:
          "Scott Cunningham (@scottcbusiness) resume: content strategy, technical copywriting, social media leadership, and AI-assisted workflows.",
      },
      { property: "og:image", content: "https://scottcbusiness.com/og-default.jpg" },
      { property: "og:image:alt", content: "Scott Cunningham, Content and Social Media Strategy" },
        { name: "twitter:image:alt", content: "Scott Cunningham, Content and Social Media Strategy" },
      { name: "twitter:image", content: "https://scottcbusiness.com/og-default.jpg" },
    ],
    links: [{ rel: "canonical", href: "https://scottcbusiness.com/resume" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://scottcbusiness.com/" },
            {
              "@type": "ListItem",
              position: 2,
              name: "Resume",
              item: "https://scottcbusiness.com/resume",
            },
          ],
        }),
      },
    ],
  }),
  component: ResumePage,
});

function ResumePage() {
  return (
    <section className="relative overflow-hidden grain">
      <div className="blob float-slow -left-32 -top-24 h-[420px] w-[420px] bg-[var(--color-primary)]" />
      <div
        className="blob float-slow right-[-120px] top-40 h-[360px] w-[360px] bg-[var(--color-electric)]"
        style={{ animationDelay: "-3s" }}
      />
      <div className="relative mx-auto max-w-5xl px-6 py-20 lg:px-10 lg:py-28">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground">
          Resume
        </p>
        <h1 className="mt-6 font-display text-[clamp(2.5rem,7vw,6rem)] leading-[0.95]">
          The <em className="italic text-gradient">short version</em>, on paper.
        </h1>
        <p className="mt-8 max-w-2xl text-lg text-muted-foreground">
          A decade of content strategy, technical copywriting, social media leadership, and
          AI-assisted workflows across AI, cloud, Web3, cybersecurity, SaaS, and consumer tech.
          Download the latest PDF below.
        </p>

        <div className="mt-12 flex flex-wrap gap-4">
          <a
            href={resumeAsset.url}
            download="Scott_Cunningham_Resume.pdf"
            className="group inline-flex items-center gap-2 rounded-full bg-gradient-hero px-6 py-3 font-mono text-xs uppercase tracking-[0.2em] text-primary-foreground shadow-lg shadow-primary/30 transition-transform hover:-translate-y-0.5"
          >
            Download resume (PDF) <Download className="h-4 w-4" />
          </a>
          <a
            href={resumeAsset.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-full border-2 border-foreground bg-background px-6 py-3 font-mono text-xs uppercase tracking-[0.2em] text-foreground shadow-pop transition-transform hover:-translate-y-0.5"
          >
            View in browser <ArrowUpRight className="h-4 w-4" />
          </a>
        </div>

        <h2 className="mt-16 font-display text-2xl sm:text-3xl">Get in touch</h2>
        <div className="mt-6 grid stat-grid gap-px rounded-md border-2 border-foreground bg-foreground shadow-pop md:grid-cols-2">
          <a
            href="mailto:scottcbusiness@protonmail.com"
            className="group flex items-center justify-between gap-4 bg-background p-8 transition-colors hover:bg-gradient-acid"
          >
            <div className="min-w-0 flex-1">
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">
                Email
              </p>
              <p className="mt-3 font-display text-lg sm:text-xl break-all">
                scottcbusiness@protonmail.com
              </p>
            </div>
            <Mail className="h-6 w-6 shrink-0" style={{ color: "var(--color-primary)" }} />
          </a>
          <a
            href="https://www.linkedin.com/in/scottcbusiness/"
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-center justify-between bg-background p-8 transition-colors hover:bg-gradient-acid"
          >
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">
                LinkedIn
              </p>
              <p className="mt-3 font-display text-xl md:text-2xl">/in/scottcbusiness</p>
            </div>
            <Linkedin className="h-6 w-6" style={{ color: "var(--color-electric)" }} />
          </a>
        </div>

        <h2 className="mt-16 font-display text-2xl sm:text-3xl">Explore more</h2>
        <div className="mt-6 flex flex-wrap items-center gap-6 font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">
          <Link to="/work" className="inline-flex items-center gap-2 text-foreground">
            See case studies <ArrowUpRight className="h-4 w-4" />
          </Link>
          <Link to="/services" className="inline-flex items-center gap-2 text-foreground">
            See services <ArrowUpRight className="h-4 w-4" />
          </Link>
          <Link to="/about" className="inline-flex items-center gap-2 text-foreground">
            About Scott <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}
